/*
 * Copyright (c) Kalaora 2022.
 * Author: fharold
 *
 * hilo-oauth-manager
 * HomePage.tsx
 */

import {PolicyListener} from "../../services/PolicyService";
import Policy from "../../tools/provider/Policy";
import HiloReactComponent from "../../tools/view/layout/HiloReactComponent";
import React from "react";
import PageTitle, {TitleTab} from "../../tools/view/layout/PageTitle";
import Category from "../../models/Category";
import CategoryAdapter, {CategoryAdapterListener} from "../adapter/CategoryAdapter";
import CategoryForm from "../../models/form/category/CategoryForm";
import UIToast from "../../tools/view/toast/UIToast";
import FormModal from "../../tools/view/layout/FormModal";
import ReactDOM from "react-dom";
import NavigationService from "../../tools/service/NavigationService";
import List from "../../tools/list/List";
import Spinner from "../../tools/view/element/Spinner";
import PageComponent from "../../tools/view/layout/PageComponent";
import CategoryService from "../../services/CategoryService";
import CategoryDataProvider from "../../provider/CategoryDataProvider";

interface State {
    policy: Policy;
    pageIndex: number;
    categoryDataProvider: CategoryDataProvider;
    categoryAdapter?: CategoryAdapter;
}

export default class CategoriesPage extends HiloReactComponent<{}, State> implements PolicyListener, CategoryAdapterListener {

    private readonly _navigationService: NavigationService;
    private readonly _categoryService: CategoryService;
    private _modalContainer?: Element;
    private _categoryForm: CategoryForm;

    /**
     * constructor
     * @param props
     * @param state
     */
    constructor(props: {}, state: State) {
        super(props, state);

        this._navigationService = this._serviceRepository.resolve(NavigationService.id) as NavigationService;
        this._categoryService = this._serviceRepository.resolve(CategoryService.id) as CategoryService;

        const categoryDataProvider = new CategoryDataProvider(this._categoryService);

        this.state = {
            policy: this._policyService.policy,
            pageIndex: 0,
            categoryDataProvider: categoryDataProvider,
            categoryAdapter: new CategoryAdapter(this.t, categoryDataProvider)
        };

        this._categoryForm = new CategoryForm()
    }

    /**
     * rowSelected
     * @param obj
     */
    public rowSelected(obj: Category): void {
        this._navigationService.load(`/category/${obj.id}`)
    }

    /**
     * componentDidMount
     */
    public componentDidMount(): void {
        super.componentDidMount();
        this._categoryService.addListener(this);
        this._modalContainer = document.querySelector('#modal-container') || undefined;
        this._policyService.addListener(this);

        if (this.state.categoryAdapter) {
            this.state.categoryAdapter.listener = this;
        }

        // this.state.categoryDataProvider.subscribe(this);
    }

    /**
     * componentWillUnmount
     */
    public componentWillUnmount(): void {
        this._policyService.removeListener(this);
        this._categoryService.removeListener(this);

        // this.state.categoryDataProvider.unsubscribe(this);

        super.componentWillUnmount();
    }

    /**
     * policyUpdated
     * @param policy
     */
    public policyUpdated = (policy: Policy) => {
        this.setState({
            policy: policy
        })
    };

    /**
     * _displayAddUserModal
     * @private
     */
    private _displayAddUserModal = async () => {
        if (!this._modalContainer || !this._categoryForm) {
            return;
        }

        this._categoryForm._clearForm();

        const modal = <FormModal
            emptyForm={this._categoryForm}
            title={t => t.newCategory}
            onSaveClicked={this._saveCategory}
        />;

        ReactDOM.render(modal, this._modalContainer);
    }

    /**
     * _categoryList
     * @returns {any}
     * @private
     */
    private _categoryList = (props: { provider?: CategoryDataProvider, adapter?: CategoryAdapter }) => {
        const {provider, adapter} = props;

        if (!adapter || !provider) return <Spinner/>;

        return <List adapter={adapter}
                     dataProvider={provider}
                     onAddClick={this._displayAddUserModal}/>
    }

    /**
     * _saveCategory
     * @param dto
     * @private
     */
    private _saveCategory = async (dto: Map<string, any> | FormData) => {
        try {
            await this._categoryService.createCategory(dto);
            if (this._categoryForm) this._categoryForm.statusCode = 201;
            UIToast.default.show(this.t.categoryAdded)

            this.state.categoryDataProvider.load()

        } catch (e) {
            if (this._categoryForm) {
                this._categoryForm.statusCode = e.status
            }
            console.error(e);
        }
    };

    /**
     * render
     */
    public render(): any {
        const {categoryDataProvider, categoryAdapter} = this.state;

        const titleTabs: TitleTab[] = [
            {title: t => t.list, content: <this._categoryList provider={categoryDataProvider} adapter={categoryAdapter}/>}
        ];

        return <PageComponent>
            <PageTitle title={t => t.categories} tabs={titleTabs}
                       onTabSelected={index => this.setState({pageIndex: index})}/>
            {
                titleTabs[this.state.pageIndex].content
            }
        </PageComponent>
    }
}